import { parseColor, formatTimestamp, eventImage, getHeaderImage, getBtvFallbackForBg } from './utils/utils';
import chroma from 'chroma-js';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';
import type { TemplateProps } from './index';
import { EPaletteModes } from '@/types/theme-packs.types';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';

export const Basic = ({
  event,
  theme,
  mode,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  startIntro,
  setStartIntro,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  const resolvedMode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = resolvedMode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[resolvedMode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = customizations?.containerColor || parseColor(themeColors.containerColor);
  const originalHeading = customizations?.headingTextColor || parseColor(themeColors.headingTextColor);
  const heading = getRelativeContrastColor(originalHeading, container);
  const originalBody = customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor);
  const body = getRelativeContrastColor(originalBody, container);
  const originalButtonTextColor =
    customizations?.buttonTextColor || parseColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor);
  const buttonTextColor = getRelativeContrastColor(originalButtonTextColor, container);
  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionDate = event.sessions[0]?.timestamp ? formatTimestamp(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(container);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont ? `${customizations.headingFont}, sans-serif` : 'Arial, sans-serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Arial, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || "YOU'VE BEEN INVITED TO");
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description;
  const _startIntro = startIntro ?? (customizations?.startIntro || 'The show starts on');
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="Basic"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--body': body,
          '--heading': heading,
          '--button-text': buttonTextColor,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--container)' }}
            >
              <tbody>
                {/* Header image */}
                <tr>
                  <td align="center" valign="middle" height={48} style={{ padding: '8px' }}>
                    {headerImg && (
                      <div
                        onClick={uploadHeaderImg}
                        data-cont="img"
                        className={loadingHeaderImg ? styles.imgLoading : undefined}
                      >
                        <img height={32} src={headerImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>

                {/* Hero text section */}
                <tr>
                  <td style={{ padding: '16px 16px 0 16px' }}>
                    <div style={{ width: '37px', borderTop: '2px solid var(--body)', fontSize: 0, lineHeight: '0' }}>
                      &nbsp;
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '24px 16px 8px 16px' }}>
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--body)',
                        fontSize: '14px',
                        fontFamily: 'var(--body-font)',
                        fontWeight: 400,
                        lineHeight: '16.8px',
                        letterSpacing: '0.42px',
                        wordWrap: 'break-word',
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0 16px 24px 16px' }}>
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '36px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        lineHeight: '36px',
                        wordWrap: 'break-word',
                      }}
                    />
                  </td>
                </tr>
                {eventDescription && (
                  <tr>
                    <td style={{ padding: '0 16px 16px 16px' }}>
                      <ContentEditable
                        value={eventDescription}
                        setValue={setDescription}
                        style={{
                          color: 'var(--body)',
                          fontSize: '14px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 400,
                          lineHeight: '20px',
                          wordWrap: 'break-word',
                        }}
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: '0 16px 16px 16px' }}>
                    <div style={{ width: '37px', borderTop: '2px solid var(--body)', fontSize: 0, lineHeight: '0' }}>
                      &nbsp;
                    </div>
                  </td>
                </tr>

                {/* Hero image */}
                <tr>
                  <td style={{ fontSize: 0, lineHeight: '0' }}>
                    {bodyImg && (
                      <div onClick={uploadMain} data-cont="img" className={loadingMain ? styles.imgLoading : undefined}>
                        <img width={520} src={bodyImg} style={{ display: 'block' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>

                {/* Date + register section */}
                <tr>
                  <td align="center" style={{ padding: '40px 24px', background: 'var(--container)' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td style={{ paddingBottom: '16px', fontSize: 0, lineHeight: '0' }}>&nbsp;</td>
                        </tr>
                        {sessionDate && (
                          <>
                            <tr>
                              <td align="center" style={{ paddingBottom: '2px' }}>
                                <ContentEditable
                                  value={_startIntro}
                                  setValue={setStartIntro}
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '11px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 600,
                                    lineHeight: '16px',
                                    letterSpacing: '0.22px',
                                    wordWrap: 'break-word',
                                    opacity: 0.75,
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td align="center" style={{ paddingBottom: '16px' }}>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionDate}
                                </div>
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td align="center">
                            <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{ border: '1px solid var(--body)', width: '148px', height: '40px' }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        textDecoration: 'none',
                                        padding: '6px 0 6px 0',
                                        width: '148px',
                                        height: '100%',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <ContentEditable
                                        value={_cta}
                                        setValue={setCta}
                                        style={{
                                          color: 'var(--button-text)',
                                          fontSize: '13px',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 600,
                                          lineHeight: '16px',
                                          wordWrap: 'break-word',
                                        }}
                                      />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Calendar links footer */}
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
